/**
 * Bank account-related Supabase queries
 * Pure async functions - no React, no useQuery
 */

import { supabase } from "@/integrations/supabase/client";

// Types
export interface BankAccount {
  id: string;
  account_name: string;
  bank_name: string;
  account_type: string;
  account_number?: string;
  is_active: boolean;
  connection_status: string;
  training_status?: string;
  created_at?: string;
  // Teller connection fields
  teller_enrollment_id?: string | null;
  teller_access_token?: string | null;
  teller_account_id?: string | null;
  teller_account_name?: string | null;
  teller_account_last_four?: string | null;
  teller_account_type?: string | null;
  teller_account_subtype?: string | null;
  // Sync fields
  sync_enabled?: boolean;
  last_synced_at?: string | null;
}

/**
 * Get all active bank accounts for a company
 */
export async function getBankAccounts(companyId: string): Promise<BankAccount[]> {
  if (!companyId) return [];

  const { data: accountsData, error: accountsError } = await supabase
    .from("bank_accounts")
    .select(`
      id, 
      account_name, 
      bank_name, 
      account_type, 
      account_number, 
      is_active, 
      connection_status, 
      created_at,
      teller_enrollment_id,
      teller_access_token,
      teller_account_id,
      teller_account_name,
      teller_account_last_four,
      teller_account_type,
      teller_account_subtype,
      sync_enabled,
      last_synced_at
    `)
    .eq("company_id", companyId)
    .eq("is_active", true)
    .order("created_at", { ascending: false });

  if (accountsError) throw accountsError;

  // Derive connection status from Teller fields if needed
  return (accountsData || []).map((account) => {
    let connectionStatus = account.connection_status;
    if (!connectionStatus) {
      if (account.teller_access_token && account.teller_account_id) {
        connectionStatus = "connected";
      } else if (account.teller_enrollment_id && !account.teller_account_id) {
        connectionStatus = "pending_selection";
      } else {
        connectionStatus = "not_connected";
      }
    }
    
    return {
      ...account,
      connection_status: connectionStatus,
    };
  }) as BankAccount[];
}

/**
 * Get all active bank accounts with training status for a company
 */
export async function getBankAccountsWithTraining(companyId: string): Promise<BankAccount[]> {
  if (!companyId) return [];

  // Fetch accounts
  const { data: accountsData, error: accountsError } = await supabase
    .from("bank_accounts")
    .select(`
      id, 
      account_name, 
      bank_name, 
      account_type, 
      account_number, 
      is_active, 
      connection_status, 
      created_at,
      teller_enrollment_id,
      teller_access_token,
      teller_account_id,
      teller_account_name,
      teller_account_last_four,
      teller_account_type,
      teller_account_subtype,
      sync_enabled,
      last_synced_at
    `)
    .eq("company_id", companyId)
    .eq("is_active", true)
    .order("created_at", { ascending: false });

  if (accountsError) throw accountsError;

  // Fetch training progress for each account
  const { data: trainingData } = await supabase
    .from("bank_account_training_progress")
    .select("account_id, status")
    .eq("company_id", companyId);

  // Also check if accounts have categorized transactions (indicates training was done)
  const accountIds = (accountsData || []).map(a => a.id);
  const { data: transactionCounts } = await supabase
    .from("bank_transactions")
    .select("account_id, category_type")
    .eq("company_id", companyId)
    .in("account_id", accountIds)
    .not("category_type", "is", null);

  // Count categorized transactions per account
  const categorizedByAccount = new Map<string, number>();
  (transactionCounts || []).forEach((tx: any) => {
    const count = categorizedByAccount.get(tx.account_id) || 0;
    categorizedByAccount.set(tx.account_id, count + 1);
  });

  // Merge training status into accounts and derive connection status if needed
  const accountsWithTraining = (accountsData || []).map((account) => {
    const training = trainingData?.find((t) => t.account_id === account.id);
    const hasCategorizedTransactions = (categorizedByAccount.get(account.id) || 0) > 0;
    
    // Consider account trained if:
    // 1. Training progress table says completed, OR
    // 2. Account has categorized transactions (training was done but progress wasn't recorded)
    let trainingStatus = training?.status || "not_started";
    if (trainingStatus !== "completed" && hasCategorizedTransactions) {
      trainingStatus = "completed";
    }
    
    // Derive connection status from Teller fields if connection_status is null
    let connectionStatus = account.connection_status;
    if (!connectionStatus) {
      if (account.teller_access_token && account.teller_account_id) {
        connectionStatus = "connected";
      } else if (account.teller_enrollment_id && !account.teller_account_id) {
        connectionStatus = "pending_selection";
      } else {
        connectionStatus = "not_connected";
      }
    }
    
    return {
      ...account,
      connection_status: connectionStatus,
      training_status: trainingStatus,
    };
  });

  return accountsWithTraining as BankAccount[];
}

/**
 * Get a single bank account by ID
 */
export async function getBankAccountById(companyId: string, accountId: string): Promise<BankAccount | null> {
  if (!companyId || !accountId) return null;

  const { data, error } = await supabase
    .from("bank_accounts")
    .select("*")
    .eq("company_id", companyId)
    .eq("id", accountId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as BankAccount;
}
